import React from 'react';
import { IconCheckmark } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';
import Link from 'next/link';

import * as styles from './Toggle.styles';

export interface ToggleProps {
  active: boolean;
  boldText?: boolean;
  disabled?: boolean;
  href?: string;
  label: string;
  name: string;
  onToggle?: (currentActive: boolean) => void;
  secondaryLabelText?: string;
  value: string;
}

export const Toggle = ({
  active,
  boldText,
  disabled,
  href,
  label: labelText,
  name,
  onToggle,
  secondaryLabelText,
  value,
}: ToggleProps) => {
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && onToggle) {
      onToggle(event.target.checked);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLLabelElement>) => {
    if (disabled || !onToggle) {
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onToggle(!active);
    }
  };

  const outerStyle = [styles.toggleItem, disabled && styles.disabled];
  const showLink = Boolean(href && !disabled);
  // Navigation link is the sole activation path (e.g. facets); label must not steal focus/keyboard.
  const linkIsPrimaryInteractive = showLink && !onToggle;

  const accessibleLinkName = `${labelText}${secondaryLabelText ? ` ${secondaryLabelText}` : ''}`;
  const toggleContent = (
    <>
      {showLink && (
        <Link
          aria-current={linkIsPrimaryInteractive && active ? 'true' : undefined}
          aria-hidden={linkIsPrimaryInteractive ? undefined : 'true'}
          css={styles.crawlLinkUnderlay}
          data-testid={`${name}-${value}-link`}
          href={href!}
          prefetch={false}
          rel="nofollow"
          scroll={false}
          shallow
          tabIndex={linkIsPrimaryInteractive ? undefined : -1}
        >
          <span css={visuallyHidden}>{accessibleLinkName}</span>
        </Link>
      )}
      <span css={styles.toggleForeground}>
        <h4
          css={styles.toggleLabel}
          style={{ '--toggleFontWeight': boldText ? '600' : '400' } as styles.ToggleFontWeightCSS}
        >
          {labelText}
          {secondaryLabelText && <span css={styles.secondaryLabel}>{secondaryLabelText}</span>}
        </h4>
        <input
          aria-hidden={linkIsPrimaryInteractive ? true : undefined}
          checked={active}
          css={styles.hiddenInput}
          disabled={disabled}
          name={name}
          onChange={handleChange}
          tabIndex={linkIsPrimaryInteractive ? -1 : undefined}
          type="checkbox"
          value={value}
        />

        {/* Visual toggle switch */}
        <div css={styles.toggleSwitch}>
          <span css={styles.toggleSlider}>
            {active && <IconCheckmark color="var(--neutral-black-base)" height={16} width={16} />}
          </span>
        </div>
      </span>
    </>
  );

  if (linkIsPrimaryInteractive) {
    return (
      <span css={outerStyle}>
        <span css={styles.toggleLabelRoot} tabIndex={-1}>
          {toggleContent}
        </span>
      </span>
    );
  }

  return (
    <span css={outerStyle}>
      <label
        aria-disabled={disabled}
        aria-pressed={active}
        css={styles.toggleLabelRoot}
        onKeyDown={handleKeyDown}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
        role="button"
        tabIndex={disabled ? -1 : 0}
      >
        {toggleContent}
      </label>
    </span>
  );
};
